import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Package, Plus, Search, Phone, Mail, Globe, Trash2 } from 'lucide-react';

import { BACKEND_URL } from '@/lib/backend';
export default function VendorsModule() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '', support_phone: '', support_email: '', website: '', account_number: '', notes: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/documentation/vendors`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) setVendors(await response.json());
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/vendors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setShowAddDialog(false);
        setFormData({ name: '', support_phone: '', support_email: '', website: '', account_number: '', notes: '' });
        loadData();
      }
    } catch (err) {
      console.error('Failed to create:', err);
    }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete vendor ${name}?`)) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/vendors/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const filtered = vendors.filter(v =>
    v.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto bg-slate-50">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Vendors</h1>
          <p className="text-sm text-slate-500 mt-1">Vendor contact directory</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <Button onClick={() => setShowAddDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" />Add Vendor
          </Button>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Add New Vendor</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Vendor Name *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Acme Software Inc." required /></div>
                <div><Label>Support Phone</Label><Input value={formData.support_phone} onChange={(e) => setFormData({...formData, support_phone: e.target.value})} placeholder="+1 (555) 123-4567" /></div>
                <div><Label>Support Email</Label><Input type="email" value={formData.support_email} onChange={(e) => setFormData({...formData, support_email: e.target.value})} placeholder="support@vendor.com" /></div>
                <div><Label>Website</Label><Input value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} placeholder="https://vendor.com" /></div>
                <div><Label>Account Number</Label><Input value={formData.account_number} onChange={(e) => setFormData({...formData, account_number: e.target.value})} /></div>
                <div className="col-span-2"><Label>Notes</Label><Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={3} /></div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">Create Vendor</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mb-6"><CardContent className="p-4"><div className="relative"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" /><Input placeholder="Search vendors..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" /></div></CardContent></Card>

      {loading ? <div className="text-center py-12"><p className="text-slate-500">Loading...</p></div> : filtered.length === 0 ? <Card><CardContent className="p-12 text-center"><Package className="w-12 h-12 mx-auto text-slate-400 mb-3" /><p className="text-slate-600 font-medium">No vendors found</p></CardContent></Card> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((vendor) => (
            <Card key={vendor.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Package className="w-5 h-5 text-indigo-600" />
                      <CardTitle className="text-lg">{vendor.name}</CardTitle>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(vendor.id, vendor.name)}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {vendor.support_phone && <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-slate-400" /><a href={`tel:${vendor.support_phone}`} className="text-slate-600">{vendor.support_phone}</a></div>}
                {vendor.support_email && <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-slate-400" /><a href={`mailto:${vendor.support_email}`} className="text-cyan-600 hover:underline">{vendor.support_email}</a></div>}
                {vendor.website && <div className="flex items-center gap-2 text-sm"><Globe className="w-4 h-4 text-slate-400" /><a href={vendor.website} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">{vendor.website.replace(/^https?:\/\//, '')}</a></div>}
                {vendor.account_number && <div className="text-xs text-slate-500 mt-2 pt-2 border-t">Account: {vendor.account_number}</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
